import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Activity,
  AlertTriangle,
  Ambulance,
  ArrowLeft,
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileDown,
  HeartPulse,
  History,
  KeyRound,
  LogOut,
  PackagePlus,
  Pill,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Search,
  Stethoscope,
  UserCheck,
  UserRound,
  UserX,
  UsersRound,
  X
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';
import { downloadCsvBlob, toCsvLine } from '../lib/csvUtils';
import { compareResidentsByKana, hasResidentKana } from '../lib/residentSort';

type ResidentStatus = 'active' | 'retired' | 'inactive';

type ResidentMaster = {
  id: string;
  resident_key: string | null;
  display_order: number | null;
  name: string;
  kana: string | null;
  room: string | null;
  last_visit_date: string | null;
  next_visit_date: string | null;
  visit_interval_days: number | null;
  active: boolean;
  notes: string | null;
  normalized_name: string | null;
  status: ResidentStatus;
  admission_date: string | null;
  admission_source_note: string | null;
  departure_date: string | null;
  departure_reason: string | null;
  departure_note: string | null;
  departure_care_level: string | null;
  care_level: string | null;
  gender: string | null;
  birth_date: string | null;
  age_at_import: number | null;
  insurer_name: string | null;
  care_record_user_key: string | null;
  care_insurance_expiry_date: string | null;
  primary_clinic: string | null;
  emergency_hospital: string | null;
  source_sheet: string | null;
  source_row: number | null;
  master_notes: string | null;
  updated_at: string | null;
};

type ResidentDeparture = {
  id: string;
  resident_id: string | null;
  resident_name_snapshot: string;
  departure_date: string | null;
  departure_care_level: string | null;
  reason: string | null;
  destination_note: string | null;
  source_sheet: string | null;
  source_row: number | null;
};

type MedicalAssignment = {
  id: string;
  resident_id: string;
  medical_provider_name: string;
  source_sheet_title: string | null;
  next_visit_date: string | null;
  latest_vital_record_date: string | null;
  active: boolean;
  notes: string | null;
};

type MedicalProvider = {
  id: string;
  name: string;
  display_order: number | null;
  active: boolean;
  notes: string | null;
  updated_at: string | null;
};

type VitalRecord = {
  id: string;
  resident_id: string | null;
  care_record_user_key: string | null;
  resident_name_snapshot: string;
  normalized_name: string;
  recorded_at: string;
  category: string | null;
  category_detail: string | null;
  reporter_name: string | null;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse: number | null;
  temperature: number | null;
  spo2: number | null;
  content: string | null;
  source_row: number | null;
  source_system: string | null;
};

type ImportIssue = {
  id: string;
  issue_type: string;
  resident_id: string | null;
  resident_name_snapshot: string | null;
  normalized_name: string | null;
  source_system: string | null;
  source_row: number | null;
  detail: Record<string, unknown> | null;
  status: string;
  created_at: string;
};

type VitalVisitInstruction = {
  id: string;
  resident_id: string;
  medical_provider_name: string;
  visit_date: string;
  vital_record_date: string | null;
  instruction: string | null;
  updated_at: string | null;
};

type EmployeeSubmission = {
  id: string;
  submission_type: 'admission' | 'departure';
  resident_id: string | null;
  resident_name_snapshot: string;
  submitted_by: string | null;
  submitted_contact: string | null;
  payload: Record<string, unknown> | null;
  status: 'applied' | 'needs_review';
  result_message: string | null;
  created_at: string;
};

type ResidentSnapshot = {
  residents: ResidentMaster[];
  departures: ResidentDeparture[];
  medicalAssignments: MedicalAssignment[];
  medicalProviders: MedicalProvider[];
  vitalLatest: VitalRecord[];
  vitalRecords: VitalRecord[];
  vitalVisitInstructions: VitalVisitInstruction[];
  issues: ImportIssue[];
  employeeSubmissions: EmployeeSubmission[];
  stats?: {
    activeResidents?: number;
    retiredResidents?: number;
    vitalRecords?: number;
    openIssues?: number;
    employeeSubmissions?: number;
  };
};

type ResidentDraft = {
  id?: string;
  resident_key: string;
  display_order: string;
  name: string;
  kana: string;
  room: string;
  status: ResidentStatus;
  admission_date: string;
  admission_source_note: string;
  departure_date: string;
  departure_reason: string;
  departure_note: string;
  care_level: string;
  gender: string;
  birth_date: string;
  insurer_name: string;
  care_record_user_key: string;
  care_insurance_expiry_date: string;
  primary_clinic: string;
  emergency_hospital: string;
  last_visit_date: string;
  next_visit_date: string;
  visit_interval_days: string;
  notes: string;
  master_notes: string;
};

type RetirementDraft = {
  resident_id: string;
  departure_date: string;
  departure_care_level: string;
  reason: string;
  destination_note: string;
};

type AssignmentDraft = {
  id?: string;
  resident_id: string;
  medical_provider_name: string;
  source_sheet_title: string;
  next_visit_date: string;
  latest_vital_record_date: string;
  active: boolean;
  notes: string;
};

type MedicalProviderDraft = {
  id?: string;
  name: string;
  display_order: string;
  active: boolean;
  notes: string;
};

type VitalDraft = {
  resident_id: string;
  recorded_at: string;
  bp_systolic: string;
  bp_diastolic: string;
  pulse: string;
  temperature: string;
  spo2: string;
  reporter_name: string;
  content: string;
};

type ProviderVitalRow = {
  resident: ResidentMaster;
  assignment: MedicalAssignment;
  vital?: VitalRecord;
  memo: string;
  instruction: string;
};

type ProviderVitalGroup = {
  provider: string;
  visitDate: string;
  vitalRecordDate: string;
  rows: ProviderVitalRow[];
};

type TabKey = 'active' | 'retired' | 'vitals' | 'issues';
type ResidentDetailTab = 'overview' | 'medical' | 'medications' | 'vitals' | 'emergency' | 'supplies' | 'history';

type ResidentMedicationSummary = {
  id: string;
  resident_id: string;
  drug_name: string;
  medication_type: string;
  prescription_source: string;
  hospital_name: string | null;
  external_hospital_name: string | null;
  dosage_text: string | null;
  timing_text: string | null;
  current_stock: number;
  stock_unit: string;
  stock_confirmed: boolean;
  days_supply: number | null;
  active: boolean;
  notes: string | null;
};

type EmergencyHubSummary = {
  profile: {
    profile_data: Record<string, unknown>;
    completed_at: string | null;
    review_due_date: string | null;
    version: number;
    updated_at: string;
  } | null;
  incidents: Array<{
    id: string;
    occurred_at: string;
    incident_data: Record<string, unknown>;
    created_by: string | null;
  }>;
  revisions: Array<{
    id: string;
    version: number;
    changed_at: string;
    changed_by: string | null;
  }>;
};

type ResidentSupplySummary = {
  monthKey: string;
  monthLabel: string;
  memo: string;
  lines: Array<{ name: string; value: string; amount: number }>;
  total: number;
};

const SESSION_KEY = 'satsuki.residents.session.v1';

const statusLabels: Record<ResidentStatus, string> = {
  active: '入居中',
  retired: '退居',
  inactive: '非表示'
};

const careLevelSelectOptions: [string, string][] = [
  ['', '未選択'],
  ['要支援1', '要支援1'],
  ['要支援2', '要支援2'],
  ['要介護1', '要介護1'],
  ['要介護2', '要介護2'],
  ['要介護3', '要介護3'],
  ['要介護4', '要介護4'],
  ['要介護5', '要介護5']
];

const issueLabels: Record<string, string> = {
  vital_unmatched_resident: 'バイタル未照合',
  vital_non_active_resident: '退居/非表示のバイタル',
  medical_assignment_unmatched_resident: '医療機関割当未照合',
  employee_departure_unmatched_resident: '従業員退居登録未照合'
};

const toDateInput = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

const toDateTimeInput = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  return value.slice(0, 10);
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateSlash = (value: string | null | undefined): string => {
  if (!value) return '-';
  const datePart = value.slice(0, 10).replace(/\//g, '-');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return value;
  return `${year}/${Number(month)}/${Number(day)}`;
};

const getDateOnly = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10).replace(/\//g, '-');
};

const firstNonEmpty = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = (value ?? '').trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const parseVitalContent = (content: string | null | undefined) => {
  const text = content ?? '';
  const bp = text.match(/【血圧高\/低】\s*(\d+)\s*\/\s*(\d+)/);
  const pulse = text.match(/【脈拍】\s*(\d+)/);
  const temperature = text.match(/【体温】\s*([0-9]+(?:\.[0-9]+)?)/);
  const spo2 = text.match(/【血中酸素飽和度】\s*(\d+)/);
  return {
    bp_systolic: bp ? bp[1] : '',
    bp_diastolic: bp ? bp[2] : '',
    pulse: pulse ? pulse[1] : '',
    temperature: temperature ? temperature[1] : '',
    spo2: spo2 ? spo2[1] : ''
  };
};

const extractVitalMemo = (content: string | null | undefined): string => {
  if (!content) return '';
  const marker = content.search(/メモ[:：]/);
  if (marker < 0) return '';
  const body = content
    .slice(marker)
    .replace(/^.*?メモ[:：]/s, '')
    .replace(/[-‐‑‒–—―ー－]{5,}/g, '\n');
  const lines = body
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const merged: string[] = [];
  lines.forEach(line => {
    const last = merged[merged.length - 1];
    if (last && line.length <= 2 && !/[。.!！?？]$/.test(last)) {
      merged[merged.length - 1] = `${last}${line}`;
    } else {
      merged.push(line);
    }
  });

  let memo = merged.join('。').replace(/。+/g, '。').trim();
  if (memo && !/[。.!！?？]$/.test(memo)) memo += '。';
  return memo;
};

const mostCommonDate = (values: Array<string | null | undefined>): string => {
  const counts = new Map<string, number>();
  values.forEach(value => {
    const date = getDateOnly(value);
    if (!date) return;
    counts.set(date, (counts.get(date) ?? 0) + 1);
  });
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || b[0].localeCompare(a[0]))[0]?.[0] ?? '';
};

const vitalInstructionKey = (provider: string, visitDate: string, residentId: string): string => `${provider}::${visitDate || 'no-date'}::${residentId}`;

const vitalPrintDensityClass = (rowCount: number): string => {
  if (rowCount >= 16) return 'vital-print-dense';
  if (rowCount >= 12) return 'vital-print-compact';
  return '';
};

const normalizeText = (value: string | null | undefined): string =>
  (value ?? '')
    .normalize('NFKC')
    .replace(/[ 　\t\r\n]/g, '')
    .replace(/髙/g, '高')
    .replace(/﨑/g, '崎')
    .replace(/惠/g, '恵')
    .toLowerCase();

const numberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const displayVitalValue = (record: VitalRecord | undefined): string => {
  if (!record) return '-';
  const parts = [
    record.bp_systolic && record.bp_diastolic ? `${record.bp_systolic}/${record.bp_diastolic}` : '',
    record.pulse ? `P ${record.pulse}` : '',
    record.spo2 ? `SpO2 ${record.spo2}` : '',
    record.temperature ? `${record.temperature}℃` : ''
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : '-';
};

const supplyCellAmount = (value: string, unitPrice: number): number => {
  const normalized = value.normalize('NFKC').trim();
  if (['○', '〇', '◯'].includes(normalized)) return unitPrice;
  const numeric = Number(normalized.replace(/[,，円\s]/g, ''));
  return normalized && Number.isFinite(numeric) ? numeric : 0;
};

const getResidentAge = (resident: ResidentMaster): string => {
  if (!resident.birth_date) {
    return resident.age_at_import ? `${resident.age_at_import}歳` : '-';
  }
  const birth = new Date(`${resident.birth_date.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return resident.age_at_import ? `${resident.age_at_import}歳` : '-';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return `${age}歳`;
};

const emptyResidentDraft = (displayOrder: number): ResidentDraft => ({
  resident_key: '',
  display_order: String(displayOrder),
  name: '',
  kana: '',
  room: '',
  status: 'active',
  admission_date: '',
  admission_source_note: '',
  departure_date: '',
  departure_reason: '',
  departure_note: '',
  care_level: '',
  gender: '',
  birth_date: '',
  insurer_name: '',
  care_record_user_key: '',
  care_insurance_expiry_date: '',
  primary_clinic: '',
  emergency_hospital: '',
  last_visit_date: '',
  next_visit_date: '',
  visit_interval_days: '14',
  notes: '',
  master_notes: ''
});

const residentToDraft = (resident: ResidentMaster): ResidentDraft => ({
  id: resident.id,
  resident_key: resident.resident_key ?? '',
  display_order: resident.display_order === null || resident.display_order === undefined ? '' : String(resident.display_order),
  name: resident.name,
  kana: resident.kana ?? '',
  room: resident.room ?? '',
  status: resident.status ?? (resident.active ? 'active' : 'inactive'),
  admission_date: toDateInput(resident.admission_date),
  admission_source_note: resident.admission_source_note ?? '',
  departure_date: toDateInput(resident.departure_date),
  departure_reason: resident.departure_reason ?? '',
  departure_note: resident.departure_note ?? '',
  care_level: resident.care_level ?? '',
  gender: resident.gender ?? '',
  birth_date: toDateInput(resident.birth_date),
  insurer_name: resident.insurer_name ?? '',
  care_record_user_key: resident.care_record_user_key ?? '',
  care_insurance_expiry_date: toDateInput(resident.care_insurance_expiry_date),
  primary_clinic: resident.primary_clinic ?? '',
  emergency_hospital: resident.emergency_hospital ?? '',
  last_visit_date: toDateInput(resident.last_visit_date),
  next_visit_date: toDateInput(resident.next_visit_date),
  visit_interval_days: resident.visit_interval_days === null || resident.visit_interval_days === undefined ? '14' : String(resident.visit_interval_days),
  notes: resident.notes ?? '',
  master_notes: resident.master_notes ?? ''
});

const emptyMedicalProviderDraft = (displayOrder: number): MedicalProviderDraft => ({
  name: '',
  display_order: String(displayOrder),
  active: true,
  notes: ''
});

const providerToDraft = (provider: MedicalProvider): MedicalProviderDraft => ({
  id: provider.id,
  name: provider.name,
  display_order: provider.display_order === null || provider.display_order === undefined ? '' : String(provider.display_order),
  active: provider.active,
  notes: provider.notes ?? ''
});

const ResidentManagementPage = () => {
  const [searchParams] = useSearchParams();
  const [sessionToken, setSessionToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem(SESSION_KEY) ?? '';
  });
  const [passcode, setPasscode] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<ResidentSnapshot>({
    residents: [],
    departures: [],
    medicalAssignments: [],
    medicalProviders: [],
    vitalLatest: [],
    vitalRecords: [],
    vitalVisitInstructions: [],
    issues: [],
    employeeSubmissions: []
  });
  const [selectedResidentId, setSelectedResidentId] = useState('');
  const [tab, setTab] = useState<TabKey>('active');
  const [detailTab, setDetailTab] = useState<ResidentDetailTab>(() => {
    const requested = searchParams.get('detail') as ResidentDetailTab | null;
    return requested && ['overview', 'medical', 'medications', 'vitals', 'emergency', 'supplies', 'history'].includes(requested)
      ? requested
      : 'overview';
  });
  const [medications, setMedications] = useState<ResidentMedicationSummary[]>([]);
  const [emergencySummary, setEmergencySummary] = useState<EmergencyHubSummary | null>(null);
  const [supplySummary, setSupplySummary] = useState<ResidentSupplySummary | null>(null);
  const [residentDetailLoading, setResidentDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [residentDraft, setResidentDraft] = useState<ResidentDraft | null>(null);
  const [retirementDraft, setRetirementDraft] = useState<RetirementDraft | null>(null);
  const [assignmentDraft, setAssignmentDraft] = useState<AssignmentDraft | null>(null);
  const [providerManagerOpen, setProviderManagerOpen] = useState(false);
  const [providerDraft, setProviderDraft] = useState<MedicalProviderDraft | null>(null);
  const [vitalDraft, setVitalDraft] = useState<VitalDraft | null>(null);
  const [importing, setImporting] = useState(false);
  const [csvPasteOpen, setCsvPasteOpen] = useState(false);
  const [csvPasteText, setCsvPasteText] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [visitDates, setVisitDates] = useState<Record<string, string>>({});
  const [visitInstructions, setVisitInstructions] = useState<Record<string, string>>({});
  const [printProvider, setPrintProvider] = useState('all');

  const authenticated = Boolean(sessionToken);
  const residents = snapshot.residents;
  const activeResidents = useMemo(() => residents.filter(resident => resident.status === 'active' && resident.active), [residents]);
  const retiredResidents = useMemo(() => residents.filter(resident => resident.status === 'retired'), [residents]);
  const residentById = useMemo(() => new Map(residents.map(resident => [resident.id, resident])), [residents]);
  const assignmentsByResident = useMemo(() => {
    const map = new Map<string, MedicalAssignment[]>();
    snapshot.medicalAssignments.forEach(assignment => {
      const rows = map.get(assignment.resident_id) ?? [];
      rows.push(assignment);
      map.set(assignment.resident_id, rows);
    });
    return map;
  }, [snapshot.medicalAssignments]);
  const latestVitalByResident = useMemo(() => {
    const map = new Map<string, VitalRecord>();
    snapshot.vitalLatest.forEach(record => {
      if (record.resident_id) map.set(record.resident_id, record);
    });
    return map;
  }, [snapshot.vitalLatest]);
  const vitalRecordsByResident = useMemo(() => {
    const map = new Map<string, VitalRecord[]>();
    snapshot.vitalRecords.forEach(record => {
      if (!record.resident_id) return;
      const rows = map.get(record.resident_id) ?? [];
      rows.push(record);
      map.set(record.resident_id, rows);
    });
    return map;
  }, [snapshot.vitalRecords]);
  const providerOptions = useMemo(() => {
    const activeProviders = snapshot.medicalProviders.filter(provider => provider.active).map(provider => provider.name);
    if (activeProviders.length) return activeProviders;
    const set = new Set<string>();
    snapshot.medicalAssignments.forEach(assignment => {
      if (assignment.active) set.add(assignment.medical_provider_name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [snapshot.medicalAssignments, snapshot.medicalProviders]);

  useEffect(() => {
    setVisitDates(prev => {
      const next = { ...prev };
      providerOptions.forEach(provider => {
        if (next[provider]) return;
        const providerAssignments = snapshot.medicalAssignments.filter(assignment => assignment.active && assignment.medical_provider_name === provider);
        next[provider] = mostCommonDate(providerAssignments.map(assignment => assignment.next_visit_date));
      });
      return next;
    });
  }, [providerOptions, snapshot.medicalAssignments]);

  useEffect(() => {
    setVisitInstructions(prev => {
      const next = { ...prev };
      snapshot.vitalVisitInstructions.forEach(item => {
        const key = vitalInstructionKey(item.medical_provider_name, getDateOnly(item.visit_date), item.resident_id);
        if (next[key] === undefined) {
          next[key] = item.instruction ?? '';
        }
      });
      return next;
    });
  }, [snapshot.vitalVisitInstructions]);

  const providerVitalGroups = useMemo<ProviderVitalGroup[]>(() => {
    return providerOptions.map(provider => {
      const providerAssignments = snapshot.medicalAssignments.filter(assignment => assignment.active && assignment.medical_provider_name === provider);
      const visitDate = visitDates[provider] ?? mostCommonDate(providerAssignments.map(assignment => assignment.next_visit_date));
      const rows = providerAssignments
        .reduce<ProviderVitalRow[]>((acc, assignment) => {
          const resident = residentById.get(assignment.resident_id);
          if (!resident || resident.status !== 'active' || !resident.active) return acc;
          const vital = latestVitalByResident.get(resident.id);
          const key = vitalInstructionKey(provider, visitDate, resident.id);
          acc.push({
            resident,
            assignment,
            vital,
            memo: extractVitalMemo(vital?.content),
            instruction: visitInstructions[key] ?? ''
          });
          return acc;
        }, [])
        .sort((a, b) => compareResidentsByKana(a.resident, b.resident));

      return {
        provider,
        visitDate,
        vitalRecordDate: mostCommonDate([
          ...providerAssignments.map(assignment => assignment.latest_vital_record_date),
          ...rows.map(row => row.vital?.recorded_at)
        ]),
        rows
      };
    });
  }, [latestVitalByResident, providerOptions, residentById, snapshot.medicalAssignments, visitDates, visitInstructions]);

  const visibleResidents = useMemo(() => {
    const source = tab === 'retired' ? retiredResidents : activeResidents;
    const query = normalizeText(search);
    return source
      .filter(resident => {
        if (!query) return true;
        const providerText = (assignmentsByResident.get(resident.id) ?? []).map(item => item.medical_provider_name).join(' ');
        return normalizeText(`${resident.name} ${resident.kana ?? ''} ${resident.room ?? ''} ${resident.care_level ?? ''} ${providerText}`).includes(query);
      })
      .filter(resident => {
        if (providerFilter === 'all') return true;
        return (assignmentsByResident.get(resident.id) ?? []).some(item => item.active && item.medical_provider_name === providerFilter);
      })
      .sort(compareResidentsByKana);
  }, [activeResidents, assignmentsByResident, providerFilter, retiredResidents, search, tab]);

  const selectedResident = useMemo(() => {
    return residentById.get(selectedResidentId) ?? visibleResidents[0] ?? activeResidents[0] ?? retiredResidents[0] ?? null;
  }, [activeResidents, residentById, retiredResidents, selectedResidentId, visibleResidents]);

  const selectedAssignments = selectedResident ? assignmentsByResident.get(selectedResident.id) ?? [] : [];
  const selectedVital = selectedResident ? latestVitalByResident.get(selectedResident.id) : undefined;
  const selectedVitalRows = selectedResident ? vitalRecordsByResident.get(selectedResident.id) ?? [] : [];
  const selectedMedications = useMemo(
    () => selectedResident
      ? medications.filter(medication => medication.resident_id === selectedResident.id && medication.active)
      : [],
    [medications, selectedResident]
  );

  const stats = useMemo(() => {
    const missingCareKey = activeResidents.filter(resident => !resident.care_record_user_key).length;
    const missingProvider = activeResidents.filter(resident => !(assignmentsByResident.get(resident.id) ?? []).some(item => item.active)).length;
    const missingVital = activeResidents.filter(resident => !latestVitalByResident.get(resident.id)).length;
    const missingKana = activeResidents.filter(resident => !hasResidentKana(resident)).length;
    return {
      active: snapshot.stats?.activeResidents ?? activeResidents.length,
      retired: snapshot.stats?.retiredResidents ?? retiredResidents.length,
      vitals: snapshot.stats?.vitalRecords ?? 0,
      issues: snapshot.stats?.openIssues ?? snapshot.issues.length,
      submissions: snapshot.stats?.employeeSubmissions ?? snapshot.employeeSubmissions.length,
      missingCareKey,
      missingProvider,
      missingVital,
      missingKana
    };
  }, [activeResidents, assignmentsByResident, latestVitalByResident, retiredResidents.length, snapshot.employeeSubmissions.length, snapshot.issues.length, snapshot.stats]);

  const loadSnapshot = async (token = sessionToken) => {
    if (!supabaseClient || !token) return;
    setLoading(true);
    try {
      const [{ data, error }, medicineResult] = await Promise.all([
        supabaseClient.rpc('resident_get_snapshot', { _session_token: token }),
        supabaseClient.rpc('medicine_get_snapshot', { _session_token: token })
      ]);
      if (error) throw error;
      const nextSnapshot = data as ResidentSnapshot;
      setSnapshot({
        residents: nextSnapshot.residents ?? [],
        departures: nextSnapshot.departures ?? [],
        medicalAssignments: nextSnapshot.medicalAssignments ?? [],
        medicalProviders: nextSnapshot.medicalProviders ?? [],
        vitalLatest: nextSnapshot.vitalLatest ?? [],
        vitalRecords: nextSnapshot.vitalRecords ?? [],
        vitalVisitInstructions: nextSnapshot.vitalVisitInstructions ?? [],
        issues: nextSnapshot.issues ?? [],
        employeeSubmissions: nextSnapshot.employeeSubmissions ?? [],
        stats: nextSnapshot.stats
      });
      if (!medicineResult.error) {
        const medicineSnapshot = medicineResult.data as { medications?: ResidentMedicationSummary[] };
        setMedications(medicineSnapshot.medications ?? []);
      }
      const requestedResidentId = searchParams.get('resident') ?? '';
      const requestedExists = nextSnapshot.residents?.some(resident => resident.id === requestedResidentId);
      setSelectedResidentId(prev => (requestedExists ? requestedResidentId : prev) || nextSnapshot.residents?.find(resident => resident.status === 'active')?.id || nextSnapshot.residents?.[0]?.id || '');
      setAuthError(null);
    } catch (error: any) {
      const message = error?.message ?? String(error);
      setAuthError(message);
      if (message.includes('認証') || error?.code === '28000') {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const showNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 2600);
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabaseClient) {
      setAuthError('Supabaseの設定が見つかりません。');
      return;
    }
    setLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabaseClient.rpc('medicine_start_session', { _passcode: passcode });
      if (error) throw error;
      const token = (data as { token: string }).token;
      setSessionToken(token);
      window.sessionStorage.setItem(SESSION_KEY, token);
      setPasscode('');
      await loadSnapshot(token);
    } catch (error: any) {
      setAuthError(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSessionToken('');
    window.sessionStorage.removeItem(SESSION_KEY);
    setSnapshot({ residents: [], departures: [], medicalAssignments: [], medicalProviders: [], vitalLatest: [], vitalRecords: [], vitalVisitInstructions: [], issues: [], employeeSubmissions: [] });
    setMedications([]);
    setEmergencySummary(null);
    setSupplySummary(null);
  };

  useEffect(() => {
    if (sessionToken) {
      void loadSnapshot(sessionToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '入居者管理 | さつきホーム';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const restorePrintTarget = () => setPrintProvider('all');
    window.addEventListener('afterprint', restorePrintTarget);
    return () => window.removeEventListener('afterprint', restorePrintTarget);
  }, []);

  useEffect(() => {
    const client = supabaseClient;
    if (!client || !sessionToken || !selectedResident) {
      setEmergencySummary(null);
      setSupplySummary(null);
      return;
    }

    let cancelled = false;
    setResidentDetailLoading(true);
    const loadResidentLinkedData = async () => {
      try {
        const emergencyPromise = client.rpc('resident_get_emergency_bundle', {
        _session_token: sessionToken,
        _resident_id: selectedResident.id
      });
        const supplyPromise = (async (): Promise<ResidentSupplySummary | null> => {
        const { data: monthData, error: monthError } = await client
          .from('supply_sales_months')
          .select('id, month_key, month_label')
          .eq('active', true)
          .order('month_key', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (monthError || !monthData) return null;

        const [{ data: rowData, error: rowError }, { data: itemData, error: itemError }] = await Promise.all([
          client
            .from('supply_sales_rows')
            .select('id, memo')
            .eq('month_id', monthData.id)
            .eq('resident_id', selectedResident.id)
            .eq('active', true)
            .maybeSingle(),
          client
            .from('supply_sales_items')
            .select('id, item_name, unit_price, item_kind')
            .eq('active', true)
            .eq('visible', true)
            .order('sort_order', { ascending: true })
        ]);
        if (rowError || itemError || !rowData) {
          return {
            monthKey: monthData.month_key,
            monthLabel: monthData.month_label,
            memo: '',
            lines: [],
            total: 0
          };
        }

        const { data: cellData, error: cellError } = await client
          .from('supply_sales_cells')
          .select('item_id, value_text')
          .eq('row_id', rowData.id);
        if (cellError) return null;
        const values = new Map((cellData ?? []).map(cell => [cell.item_id as string, String(cell.value_text ?? '')]));
        const lines = (itemData ?? []).flatMap(item => {
          const value = values.get(item.id as string)?.trim() ?? '';
          if (!value) return [];
          const amount = item.item_kind === 'charge' ? supplyCellAmount(value, Number(item.unit_price ?? 0)) : 0;
          return [{ name: String(item.item_name), value, amount }];
        });
        return {
          monthKey: monthData.month_key,
          monthLabel: monthData.month_label,
          memo: String(rowData.memo ?? ''),
          lines,
          total: lines.reduce((sum, line) => sum + line.amount, 0)
        };
        })();

        const [emergencyResult, nextSupplySummary] = await Promise.all([emergencyPromise, supplyPromise]);
        if (cancelled) return;
        setEmergencySummary(emergencyResult.error ? null : emergencyResult.data as EmergencyHubSummary);
        setSupplySummary(nextSupplySummary);
      } catch {
        if (!cancelled) {
          setEmergencySummary(null);
          setSupplySummary(null);
        }
      } finally {
        if (!cancelled) setResidentDetailLoading(false);
      }
    };
    void loadResidentLinkedData();
    return () => {
      cancelled = true;
    };
  }, [selectedResident, sessionToken]);

  const saveResident = async () => {
    if (!supabaseClient || !sessionToken || !residentDraft) return;
    if (!residentDraft.name.trim()) {
      alert('入居者名を入力してください');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...residentDraft,
        display_order: numberOrNull(residentDraft.display_order),
        active: residentDraft.status === 'active',
        visit_interval_days: numberOrNull(residentDraft.visit_interval_days) ?? 14,
        admission_date: residentDraft.admission_date || null,
        departure_date: residentDraft.departure_date || null,
        birth_date: residentDraft.birth_date || null,
        care_insurance_expiry_date: residentDraft.care_insurance_expiry_date || null,
        last_visit_date: residentDraft.last_visit_date || null,
        next_visit_date: residentDraft.next_visit_date || null
      };
      const { error } = await supabaseClient.rpc('resident_save', {
        _session_token: sessionToken,
        _resident: payload
      });
      if (error) throw error;
      setResidentDraft(null);
      await loadSnapshot();
      showNotice('入居者情報を保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const retireResident = async () => {
    if (!supabaseClient || !sessionToken || !retirementDraft) return;
    if (!retirementDraft.departure_date) {
      alert('退居日を入力してください');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('resident_retire', {
        _session_token: sessionToken,
        _resident_id: retirementDraft.resident_id,
        _departure: {
          departure_date: retirementDraft.departure_date,
          departure_care_level: retirementDraft.departure_care_level,
          reason: retirementDraft.reason,
          destination_note: retirementDraft.destination_note,
          source_sheet: 'browser'
        }
      });
      if (error) throw error;
      setRetirementDraft(null);
      setTab('retired');
      await loadSnapshot();
      showNotice('退居登録しました');
    } catch (error: any) {
      alert(`退居登録に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const restoreResident = async (resident: ResidentMaster) => {
    if (!supabaseClient || !sessionToken) return;
    if (!window.confirm(`${resident.name}さんを入居中に戻しますか？`)) return;
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('resident_restore', {
        _session_token: sessionToken,
        _resident_id: resident.id
      });
      if (error) throw error;
      setTab('active');
      await loadSnapshot();
      showNotice('入居中に戻しました');
    } catch (error: any) {
      alert(`復帰に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const saveAssignment = async () => {
    if (!supabaseClient || !sessionToken || !assignmentDraft) return;
    if (!assignmentDraft.medical_provider_name.trim()) {
      alert('医療機関名を入力してください');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('resident_save_medical_assignment', {
        _session_token: sessionToken,
        _assignment: {
          ...assignmentDraft,
          next_visit_date: assignmentDraft.next_visit_date || null,
          latest_vital_record_date: assignmentDraft.latest_vital_record_date || null
        }
      });
      if (error) throw error;
      setAssignmentDraft(null);
      await loadSnapshot();
      showNotice('医療機関割当を保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const saveMedicalProvider = async () => {
    if (!supabaseClient || !sessionToken || !providerDraft) return;
    if (!providerDraft.name.trim()) {
      alert('医療機関名を入力してください');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('resident_save_medical_provider', {
        _session_token: sessionToken,
        _provider: {
          ...providerDraft,
          display_order: numberOrNull(providerDraft.display_order)
        }
      });
      if (error) throw error;
      setProviderDraft(null);
      await loadSnapshot();
      showNotice('クリニック一覧を保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const saveVital = async () => {
    if (!supabaseClient || !sessionToken || !vitalDraft) return;
    if (!vitalDraft.recorded_at) {
      alert('記録日時を入力してください');
      return;
    }
    const resident = residentById.get(vitalDraft.resident_id);
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('resident_save_vital_record', {
        _session_token: sessionToken,
        _record: {
          ...vitalDraft,
          resident_name_snapshot: resident?.name ?? '',
          care_record_user_key: resident?.care_record_user_key ?? null,
          category: 'バイタル記録',
          bp_systolic: numberOrNull(vitalDraft.bp_systolic),
          bp_diastolic: numberOrNull(vitalDraft.bp_diastolic),
          pulse: numberOrNull(vitalDraft.pulse),
          temperature: numberOrNull(vitalDraft.temperature),
          spo2: numberOrNull(vitalDraft.spo2),
          source_system: 'browser_manual'
        }
      });
      if (error) throw error;
      setVitalDraft(null);
      await loadSnapshot();
      showNotice('バイタルを保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const exportResidentsCsv = () => {
    const rows = [
      ['状態', '表示順', '氏名', 'かな', '居室', '入居日', '退居日', '介護度', '性別', '生年月日', '年齢', '利用者キー', '担当医療機関', '最新バイタル', '最終記録日時'],
      ...residents.map(resident => {
        const assignments = assignmentsByResident.get(resident.id) ?? [];
        const latest = latestVitalByResident.get(resident.id);
        return [
          statusLabels[resident.status ?? 'inactive'],
          resident.display_order ?? '',
          resident.name,
          resident.kana ?? '',
          resident.room ?? '',
          formatDate(resident.admission_date),
          formatDate(resident.departure_date),
          resident.care_level ?? '',
          resident.gender ?? '',
          formatDate(resident.birth_date),
          getResidentAge(resident),
          resident.care_record_user_key ?? '',
          assignments.filter(item => item.active).map(item => item.medical_provider_name).join(' / '),
          displayVitalValue(latest),
          latest ? formatDateTime(latest.recorded_at) : ''
        ];
      })
    ];
    downloadCsvBlob(rows.map(toCsvLine).join('\n'), `satsuki-residents-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const importCareCsvRows = async (rows: Record<string, string>[]) => {
    if (!supabaseClient || !sessionToken) return;
    setImporting(true);
    try {
      const vitalRows = rows
        .map(row => {
          const content = firstNonEmpty(row.content, row['内容']);
          const parsed = parseVitalContent(content);
          return {
            source_row: firstNonEmpty(row.source_row),
            care_record_user_key: firstNonEmpty(row.care_record_user_key, row['利用者キー']),
            resident_name: firstNonEmpty(row.resident_name, row['利用者氏名']),
            recorded_at: firstNonEmpty(row.recorded_at, row['記録日時']),
            category: firstNonEmpty(row.category, row['分類']) || 'バイタル記録',
            category_detail: firstNonEmpty(row.category_detail, row['分類補助情報']),
            reporter_name: firstNonEmpty(row.reporter_name, row['報告者']),
            bp_systolic: firstNonEmpty(row.bp_systolic, parsed.bp_systolic),
            bp_diastolic: firstNonEmpty(row.bp_diastolic, parsed.bp_diastolic),
            pulse: firstNonEmpty(row.pulse, parsed.pulse),
            temperature: firstNonEmpty(row.temperature, parsed.temperature),
            spo2: firstNonEmpty(row.spo2, parsed.spo2),
            content,
            source_system: 'browser_csv_import'
          };
        })
        .filter(row => row.category === 'バイタル記録' || Boolean(row.bp_systolic || row.bp_diastolic || row.pulse || row.temperature || row.spo2))
        .filter(row => row.resident_name && row.recorded_at);

      let imported = 0;
      let issues = 0;
      for (let index = 0; index < vitalRows.length; index += 300) {
        const chunk = vitalRows.slice(index, index + 300);
        const { data, error } = await supabaseClient.rpc('resident_import_vital_records', {
          _session_token: sessionToken,
          _records: chunk
        });
        if (error) throw error;
        imported += Number((data as any)?.imported ?? chunk.length);
        issues += Number((data as any)?.issues ?? 0);
      }
      await loadSnapshot();
      showNotice(`バイタルCSVを取り込みました: ${imported}件 / 要確認 ${issues}件`);
    } catch (error: any) {
      alert(`CSV取込に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setImporting(false);
    }
  };

  const handleCareCsvPaste = async () => {
    if (!csvPasteText.trim()) {
      alert('CSVを貼り付けてください');
      return;
    }
    await importCareCsvRows(parseCsv(csvPasteText.replace(/^\uFEFF/, '')));
    setCsvPasteText('');
    setCsvPasteOpen(false);
  };

  const saveVitalVisitInstructions = async (provider: string) => {
    if (!supabaseClient || !sessionToken) return;
    const group = providerVitalGroups.find(item => item.provider === provider);
    if (!group) return;
    if (!group.visitDate) {
      alert('往診日を入力してください');
      return;
    }
    setSavingInstructions(true);
    try {
      const records = group.rows.map(row => ({
        resident_id: row.resident.id,
        medical_provider_name: provider,
        visit_date: group.visitDate,
        vital_record_date: group.vitalRecordDate || null,
        instruction: visitInstructions[vitalInstructionKey(provider, group.visitDate, row.resident.id)] ?? ''
      }));
      const { error } = await supabaseClient.rpc('resident_save_vital_visit_instructions', {
        _session_token: sessionToken,
        _records: records
      });
      if (error) throw error;
      await loadSnapshot();
      showNotice(`${provider}の往診時指示を保存しました`);
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setSavingInstructions(false);
    }
  };

  const printVitalProvider = (provider: string) => {
    setPrintProvider(provider);
    window.setTimeout(() => window.print(), 80);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900">
        <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-cyan-700 text-white">
              <UsersRound className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">入居者管理</h1>
              <p className="text-sm text-slate-500">さつきホーム</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              name="username"
              autoComplete="username"
              value="satsuki-resident-admin"
              readOnly
              className="hidden"
              tabIndex={-1}
            />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">管理パスコード</span>
              <input
                id="resident-management-passcode"
                name="resident-management-passcode"
                type="password"
                autoComplete="current-password"
                value={passcode}
                onChange={event => setPasscode(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-cyan-700"
                autoFocus
              />
            </label>
            {authError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 font-medium text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <KeyRound className="h-4 w-4" />
              開く
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 resident-management-page">
      <header className="border-b border-slate-200 bg-white resident-screen-only">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-cyan-700 text-white">
              <UsersRound className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">入居者管理</h1>
              <p className="text-sm text-slate-500">入居者・退居者・バイタル共通マスター</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {notice && <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">{notice}</span>}
            <Link to="/care" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" />
              ケアトップ
            </Link>
            <Link to="/admin/medicine" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <Pill className="h-4 w-4" />
              残薬管理
            </Link>
            <Link to="/admin/emergency" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <Ambulance className="h-4 w-4" />
              救急情報
            </Link>
            <Link to="/admin/supplies" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <PackagePlus className="h-4 w-4" />
              物品販売
            </Link>
            <Link to="/resident-entry" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <ClipboardCheck className="h-4 w-4" />
              入退去登録
            </Link>
            <button onClick={() => setProviderManagerOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <Building2 className="h-4 w-4" />
              クリニック管理
            </button>
            <button onClick={() => loadSnapshot()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              更新
            </button>
            <button onClick={exportResidentsCsv} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <FileDown className="h-4 w-4" />
              CSV
            </button>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
              <LogOut className="h-4 w-4" />
              ロック
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-4 py-4 sm:px-6 resident-management-main">
        {authError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {authError}
          </div>
        )}

        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-8 resident-screen-only">
          <SummaryTile label="現入居者" value={stats.active} icon={<UserCheck className="h-4 w-4" />} />
          <SummaryTile label="退居者" value={stats.retired} icon={<UserX className="h-4 w-4" />} />
          <SummaryTile label="バイタル" value={stats.vitals} icon={<HeartPulse className="h-4 w-4" />} />
          <SummaryTile label="要確認" value={stats.issues} tone="red" icon={<AlertTriangle className="h-4 w-4" />} />
          <SummaryTile label="登録履歴" value={stats.submissions} icon={<ClipboardCheck className="h-4 w-4" />} />
          <SummaryTile label="利用者キー未設定" value={stats.missingCareKey} tone="amber" icon={<ClipboardList className="h-4 w-4" />} />
          <SummaryTile label="医療機関未設定" value={stats.missingProvider} tone="amber" icon={<Building2 className="h-4 w-4" />} />
          <SummaryTile label="ふりがな未設定" value={stats.missingKana} tone="amber" icon={<UserRound className="h-4 w-4" />} />
        </div>

        <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)] resident-management-grid">
          <aside className="space-y-3 resident-screen-only">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-3 flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                {([
                  ['active', '現入居者'],
                  ['retired', '退居者'],
                  ['vitals', 'バイタル'],
                  ['issues', '要確認']
                ] as [TabKey, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium ${tab === key ? 'bg-white text-cyan-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="mb-3 flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="検索"
                    className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-cyan-700"
                  />
                </div>
                <button
                  onClick={() => setResidentDraft(emptyResidentDraft(residents.length + 1))}
                  className="grid h-9 w-9 place-items-center rounded-lg bg-cyan-700 text-white hover:bg-cyan-800"
                  title="入居者追加"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <select
                value={providerFilter}
                onChange={event => setProviderFilter(event.target.value)}
                className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-700"
              >
                <option value="all">医療機関すべて</option>
                {providerOptions.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>

              <div className="max-h-[calc(100vh-340px)] space-y-1 overflow-auto pr-1">
                {(tab === 'issues' ? issueResidentRows(snapshot.issues, residentById) : visibleResidents).map((item, index) => {
                  if ('issue_type' in item) {
                    return <IssueListRow key={item.id} issue={item} />;
                  }
                  const resident = item as ResidentMaster;
                  const assignments = assignmentsByResident.get(resident.id) ?? [];
                  const latest = latestVitalByResident.get(resident.id);
                  return (
                    <button
                      key={resident.id}
                      onClick={() => setSelectedResidentId(resident.id)}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                        selectedResident?.id === resident.id
                          ? 'border-cyan-300 bg-cyan-50'
                          : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-start gap-2">
                          <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-md bg-slate-100 text-[11px] font-bold text-slate-600">{index + 1}</span>
                          <div className="min-w-0">
                            <div className="truncate font-semibold">{resident.name}</div>
                            <div className={`mt-0.5 truncate text-xs ${hasResidentKana(resident) ? 'text-slate-400' : 'font-semibold text-amber-700'}`}>
                              {resident.kana || 'ふりがな未登録'}
                            </div>
                          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500">
                            <span>{resident.care_level || '-'}</span>
                            <span>{assignments.filter(row => row.active).map(row => row.medical_provider_name).join(' / ') || '医療機関未設定'}</span>
                          </div>
                          </div>
                        </div>
                        <StatusBadge status={resident.status} />
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {latest ? `${formatDateTime(latest.recorded_at)} ${displayVitalValue(latest)}` : 'バイタル未登録'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="min-w-0 resident-content-panel">
            {tab === 'vitals' ? (
              <VitalRecordsPanel
                records={snapshot.vitalRecords}
                residentById={residentById}
                groups={providerVitalGroups}
                visitDates={visitDates}
                printProvider={printProvider}
                loading={importing}
                savingInstructions={savingInstructions}
                onPasteClick={() => setCsvPasteOpen(true)}
                onVisitDateChange={(provider, value) => setVisitDates(prev => ({ ...prev, [provider]: value }))}
                onInstructionChange={(provider, visitDate, residentId, value) => setVisitInstructions(prev => ({ ...prev, [vitalInstructionKey(provider, visitDate, residentId)]: value }))}
                onSaveInstructions={saveVitalVisitInstructions}
                onPrintProvider={printVitalProvider}
              />
            ) : tab === 'issues' ? (
              <IssuesPanel issues={snapshot.issues} submissions={snapshot.employeeSubmissions} residentById={residentById} />
            ) : selectedResident ? (
              <ResidentDetailPanel
                resident={selectedResident}
                assignments={selectedAssignments}
                latestVital={selectedVital}
                vitalRows={selectedVitalRows}
                departures={snapshot.departures.filter(item => item.resident_id === selectedResident.id)}
                detailTab={detailTab}
                medications={selectedMedications}
                emergencySummary={emergencySummary}
                supplySummary={supplySummary}
                linkedDataLoading={residentDetailLoading}
                onDetailTabChange={setDetailTab}
                onEdit={() => setResidentDraft(residentToDraft(selectedResident))}
                onRetire={() => setRetirementDraft({
                  resident_id: selectedResident.id,
                  departure_date: new Date().toISOString().slice(0, 10),
                  departure_care_level: selectedResident.care_level ?? '',
                  reason: '',
                  destination_note: ''
                })}
                onRestore={() => restoreResident(selectedResident)}
                onAddAssignment={() => setAssignmentDraft({
                  resident_id: selectedResident.id,
                  medical_provider_name: '',
                  source_sheet_title: '',
                  next_visit_date: '',
                  latest_vital_record_date: '',
                  active: true,
                  notes: ''
                })}
                onEditAssignment={assignment => setAssignmentDraft({
                  id: assignment.id,
                  resident_id: assignment.resident_id,
                  medical_provider_name: assignment.medical_provider_name,
                  source_sheet_title: assignment.source_sheet_title ?? '',
                  next_visit_date: toDateInput(assignment.next_visit_date),
                  latest_vital_record_date: toDateInput(assignment.latest_vital_record_date),
                  active: assignment.active,
                  notes: assignment.notes ?? ''
                })}
                onAddVital={() => setVitalDraft({
                  resident_id: selectedResident.id,
                  recorded_at: toDateTimeInput(new Date().toISOString()),
                  bp_systolic: '',
                  bp_diastolic: '',
                  pulse: '',
                  temperature: '',
                  spo2: '',
                  reporter_name: '',
                  content: ''
                })}
              />
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
                入居者を選択してください
              </div>
            )}
          </section>
        </div>
      </main>

      {csvPasteOpen && (
        <ModalShell title="CSV取込" icon={<ClipboardList className="h-5 w-5" />} onClose={() => setCsvPasteOpen(false)}>
          <textarea
            value={csvPasteText}
            onChange={event => setCsvPasteText(event.target.value)}
            rows={14}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-cyan-700"
            placeholder="CSVのヘッダー行から貼り付け"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setCsvPasteOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">閉じる</button>
            <button
              onClick={handleCareCsvPaste}
              disabled={importing}
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {importing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
              取り込む
            </button>
          </div>
        </ModalShell>
      )}

      {residentDraft && (
        <ResidentModal
          draft={residentDraft}
          medicalProviderOptions={providerOptions}
          onChange={setResidentDraft}
          onClose={() => setResidentDraft(null)}
          onSave={saveResident}
        />
      )}

      {retirementDraft && selectedResident && (
        <RetirementModal
          resident={selectedResident}
          draft={retirementDraft}
          onChange={setRetirementDraft}
          onClose={() => setRetirementDraft(null)}
          onSave={retireResident}
        />
      )}

      {assignmentDraft && (
        <AssignmentModal
          draft={assignmentDraft}
          medicalProviderOptions={providerOptions}
          onChange={setAssignmentDraft}
          onClose={() => setAssignmentDraft(null)}
          onSave={saveAssignment}
        />
      )}

      {providerManagerOpen && (
        <MedicalProviderModal
          providers={snapshot.medicalProviders}
          draft={providerDraft}
          onDraftChange={setProviderDraft}
          onNew={() => setProviderDraft(emptyMedicalProviderDraft((snapshot.medicalProviders.length + 1) * 10))}
          onEdit={provider => setProviderDraft(providerToDraft(provider))}
          onClose={() => {
            setProviderManagerOpen(false);
            setProviderDraft(null);
          }}
          onSave={saveMedicalProvider}
        />
      )}

      {vitalDraft && (
        <VitalModal
          draft={vitalDraft}
          onChange={setVitalDraft}
          onClose={() => setVitalDraft(null)}
          onSave={saveVital}
        />
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, icon, tone = 'slate' }: { label: string; value: number; icon: ReactNode; tone?: 'slate' | 'red' | 'amber' }) => {
  const toneClass = tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-cyan-800';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className={`mb-2 flex items-center gap-2 text-sm font-medium ${toneClass}`}>
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900">{value.toLocaleString('ja-JP')}</div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: ResidentStatus }) => {
  const className =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : status === 'retired'
        ? 'bg-slate-100 text-slate-700 ring-slate-200'
        : 'bg-amber-50 text-amber-700 ring-amber-200';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${className}`}>{statusLabels[status] ?? status}</span>;
};

const providerSelectOptions = (providers: string[], currentValue = ''): [string, string][] => {
  const names = Array.from(new Set([...providers, currentValue].filter(Boolean)));
  return [['', '未選択'], ...names.map(name => [name, name] as [string, string])];
};

const emergencyProfileValue = (summary: EmergencyHubSummary | null, key: string): string => {
  const value = summary?.profile?.profile_data?.[key];
  return typeof value === 'string' && value.trim() ? value : '-';
};

const medicationSourceLabel = (value: string): string => ({
  home_visit: '往診医',
  external_1: '外部受診①',
  external_2: '外部受診②',
  external_3: '外部受診③'
}[value] ?? value);

const ResidentDetailPanel = ({
  resident,
  assignments,
  latestVital,
  vitalRows,
  departures,
  detailTab,
  medications,
  emergencySummary,
  supplySummary,
  linkedDataLoading,
  onDetailTabChange,
  onEdit,
  onRetire,
  onRestore,
  onAddAssignment,
  onEditAssignment,
  onAddVital
}: {
  resident: ResidentMaster;
  assignments: MedicalAssignment[];
  latestVital?: VitalRecord;
  vitalRows: VitalRecord[];
  departures: ResidentDeparture[];
  detailTab: ResidentDetailTab;
  medications: ResidentMedicationSummary[];
  emergencySummary: EmergencyHubSummary | null;
  supplySummary: ResidentSupplySummary | null;
  linkedDataLoading: boolean;
  onDetailTabChange: (tab: ResidentDetailTab) => void;
  onEdit: () => void;
  onRetire: () => void;
  onRestore: () => void;
  onAddAssignment: () => void;
  onEditAssignment: (assignment: MedicalAssignment) => void;
  onAddVital: () => void;
}) => {
  const detailTabs: Array<[ResidentDetailTab, string]> = [
    ['overview', '概要'],
    ['medical', '医療機関'],
    ['medications', '残薬'],
    ['vitals', 'バイタル'],
    ['emergency', '救急情報'],
    ['supplies', '物品販売'],
    ['history', '履歴']
  ];

  return (
  <div className="space-y-4">
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <UserRound className="h-5 w-5 text-cyan-700" />
            <h2 className="text-2xl font-bold">{resident.name}</h2>
            <StatusBadge status={resident.status} />
          </div>
          <div className="flex flex-wrap gap-2 text-sm text-slate-500">
            <span>{resident.kana || 'かな未設定'}</span>
            <span>居室 {resident.room || '-'}</span>
            <span>{resident.care_level || '介護度未設定'}</span>
            <span>{resident.gender || '-'}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onEdit} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50">
            <Save className="h-4 w-4" />
            編集
          </button>
          <button onClick={onAddVital} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50">
            <HeartPulse className="h-4 w-4" />
            バイタル
          </button>
          {resident.status === 'active' ? (
            <button onClick={onRetire} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
              <UserX className="h-4 w-4" />
              退居
            </button>
          ) : (
            <button onClick={onRestore} className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-800">
              <UserCheck className="h-4 w-4" />
              復帰
            </button>
          )}
        </div>
      </div>
    </div>

    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white px-2 pt-2">
      <div className="flex min-w-max gap-1 border-b border-slate-200">
        {detailTabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onDetailTabChange(key)}
            className={`min-w-[92px] border-b-2 px-3 py-2.5 text-sm font-medium ${detailTab === key ? 'border-cyan-700 text-cyan-800' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {detailTab === 'overview' && (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <InfoBand label="入居日" value={formatDate(resident.admission_date)} icon={<CalendarClock className="h-4 w-4" />} />
          <InfoBand label="生年月日/年齢" value={`${formatDate(resident.birth_date)} / ${getResidentAge(resident)}`} icon={<UserRound className="h-4 w-4" />} />
          <InfoBand label="利用者キー" value={resident.care_record_user_key || '-'} icon={<ClipboardList className="h-4 w-4" />} />
          <InfoBand label="最新バイタル" value={displayVitalValue(latestVital)} icon={<Activity className="h-4 w-4" />} />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <FieldBlock label="入居元/共有事項" value={resident.admission_source_note || resident.master_notes || '-'} />
          <FieldBlock label="担当クリニック/緊急時病院" value={`${resident.primary_clinic || '-'} / ${resident.emergency_hospital || '-'}`} />
          <FieldBlock label="介護保険証期限" value={formatDate(resident.care_insurance_expiry_date)} />
          <FieldBlock label="最終更新" value={formatDateTime(resident.updated_at)} />
        </div>
        {resident.status === 'retired' && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <h3 className="mb-3 flex items-center gap-2 font-bold"><UserX className="h-4 w-4 text-slate-600" />退居情報</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <FieldBlock label="退居日" value={formatDate(resident.departure_date)} />
              <FieldBlock label="退居時介護度" value={resident.departure_care_level || resident.care_level || '-'} />
              <FieldBlock label="退居理由" value={resident.departure_reason || '-'} />
              <FieldBlock label="共有事項" value={resident.departure_note || '-'} />
            </div>
          </div>
        )}
      </div>
    )}

    {detailTab === 'medical' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-bold"><Stethoscope className="h-4 w-4 text-cyan-700" />医療機関</h3>
        <button onClick={onAddAssignment} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50">
          <Plus className="h-4 w-4" />
          追加
        </button>
      </div>
      <div className="max-h-[60vh] overflow-auto overscroll-contain">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm">
            <tr>
              <th className="px-3 py-2">医療機関</th>
              <th className="px-3 py-2">次回往診</th>
              <th className="px-3 py-2">記録日</th>
              <th className="px-3 py-2">状態</th>
              <th className="px-3 py-2 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map(assignment => (
              <tr key={assignment.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{assignment.medical_provider_name}</td>
                <td className="px-3 py-2">{formatDate(assignment.next_visit_date)}</td>
                <td className="px-3 py-2">{formatDate(assignment.latest_vital_record_date)}</td>
                <td className="px-3 py-2">{assignment.active ? '有効' : '停止'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => onEditAssignment(assignment)} className="rounded-md px-2 py-1 text-cyan-700 hover:bg-cyan-50">編集</button>
                </td>
              </tr>
            ))}
            {assignments.length === 0 && (
              <tr><td className="px-3 py-6 text-center text-slate-500" colSpan={5}>医療機関割当がありません</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>}

    {detailTab === 'medications' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-bold"><Pill className="h-4 w-4 text-teal-700" />残薬・処方薬</h3>
        <Link to={`/admin/medicine?resident=${resident.id}`} className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium text-white hover:bg-teal-800"><Pill className="h-4 w-4" />この入居者の残薬を編集</Link>
      </div>
      <div className="max-h-[60vh] overflow-auto overscroll-contain">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm"><tr><th className="px-3 py-2">薬剤</th><th className="px-3 py-2">処方元</th><th className="px-3 py-2">用法</th><th className="px-3 py-2">現在庫</th><th className="px-3 py-2">確認</th></tr></thead>
          <tbody>
            {medications.map(medication => <tr key={medication.id} className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium">{medication.drug_name}</td>
              <td className="px-3 py-2">{medicationSourceLabel(medication.prescription_source)}{(medication.hospital_name || medication.external_hospital_name) ? ` / ${medication.hospital_name || medication.external_hospital_name}` : ''}</td>
              <td className="px-3 py-2">{[medication.dosage_text, medication.timing_text].filter(Boolean).join(' / ') || '-'}</td>
              <td className="px-3 py-2 font-semibold">{medication.current_stock} {medication.stock_unit}</td>
              <td className="px-3 py-2">{medication.stock_confirmed ? '確認済' : '要確認'}</td>
            </tr>)}
            {medications.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">有効な残薬記録はありません</td></tr>}
          </tbody>
        </table>
      </div>
    </div>}

    {detailTab === 'vitals' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-bold"><HeartPulse className="h-4 w-4 text-cyan-700" />バイタル履歴</h3>
        <button onClick={onAddVital} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50"><Plus className="h-4 w-4" />追加</button>
      </div>
      <VitalTable records={vitalRows} />
    </div>}

    {detailTab === 'emergency' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-bold"><Ambulance className="h-4 w-4 text-rose-700" />救急情報</h3>
        <Link to={`/admin/emergency?resident=${resident.id}`} className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-800"><Ambulance className="h-4 w-4" />救急情報を入力・印刷</Link>
      </div>
      {linkedDataLoading ? <p className="py-8 text-center text-slate-500">読み込み中...</p> : <>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <InfoBand label="基本情報" value={emergencySummary?.profile?.completed_at ? '入力完了' : '未完了'} icon={<ClipboardCheck className="h-4 w-4" />} />
          <InfoBand label="次回見直し日" value={formatDate(emergencySummary?.profile?.review_due_date)} icon={<CalendarClock className="h-4 w-4" />} />
          <InfoBand label="救急要請履歴" value={`${emergencySummary?.incidents.length ?? 0}件`} icon={<History className="h-4 w-4" />} />
          <InfoBand label="最終更新" value={formatDateTime(emergencySummary?.profile?.updated_at)} icon={<RefreshCw className="h-4 w-4" />} />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <FieldBlock label="緊急連絡先1" value={`${emergencyProfileValue(emergencySummary, 'emergency1Name')} / ${emergencyProfileValue(emergencySummary, 'emergency1Relation')} / ${emergencyProfileValue(emergencySummary, 'emergency1Tel')}`} />
          <FieldBlock label="緊急連絡先2" value={`${emergencyProfileValue(emergencySummary, 'emergency2Name')} / ${emergencyProfileValue(emergencySummary, 'emergency2Relation')} / ${emergencyProfileValue(emergencySummary, 'emergency2Tel')}`} />
          <FieldBlock label="アレルギー" value={`${emergencyProfileValue(emergencySummary, 'allergy')} / ${emergencyProfileValue(emergencySummary, 'allergen')}`} />
          <FieldBlock label="医療機関" value={`${emergencyProfileValue(emergencySummary, 'provider1Name')} / ${emergencyProfileValue(emergencySummary, 'provider2Name')}`} />
        </div>
      </>}
    </div>}

    {detailTab === 'supplies' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-bold"><PackagePlus className="h-4 w-4 text-amber-700" />物品販売</h3>
        <Link to={`/admin/supplies?resident=${resident.id}`} className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"><PackagePlus className="h-4 w-4" />この入居者の物品を編集</Link>
      </div>
      {linkedDataLoading ? <p className="py-8 text-center text-slate-500">読み込み中...</p> : <>
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <InfoBand label="対象月" value={supplySummary?.monthLabel || '-'} icon={<CalendarClock className="h-4 w-4" />} />
          <InfoBand label="請求対象" value={`${supplySummary?.lines.length ?? 0}項目`} icon={<ClipboardList className="h-4 w-4" />} />
          <InfoBand label="合計" value={`${(supplySummary?.total ?? 0).toLocaleString('ja-JP')}円`} icon={<PackagePlus className="h-4 w-4" />} />
        </div>
        <div className="max-h-[60vh] overflow-auto overscroll-contain"><table className="w-full min-w-[560px] text-sm"><thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm"><tr><th className="px-3 py-2">品目</th><th className="px-3 py-2">入力値</th><th className="px-3 py-2 text-right">金額</th></tr></thead><tbody>
          {(supplySummary?.lines ?? []).map(line => <tr key={line.name} className="border-t border-slate-100"><td className="px-3 py-2 font-medium">{line.name}</td><td className="px-3 py-2">{line.value}</td><td className="px-3 py-2 text-right">{line.amount ? `${line.amount.toLocaleString('ja-JP')}円` : '-'}</td></tr>)}
          {(supplySummary?.lines.length ?? 0) === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-500">当月の物品販売記録はありません</td></tr>}
        </tbody></table></div>
        {supplySummary?.memo && <div className="mt-3"><FieldBlock label="メモ" value={supplySummary.memo} /></div>}
      </>}
    </div>}

    {detailTab === 'history' && <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-4 flex items-center gap-2 font-bold"><History className="h-4 w-4 text-slate-700" />入居者履歴</h3>
      <div className="space-y-2 text-sm">
        <HistoryRow date={resident.admission_date} title="入居" detail={resident.admission_source_note || '入居者マスターに登録'} />
        {(emergencySummary?.incidents ?? []).map(incident => <HistoryRow key={incident.id} date={incident.occurred_at} title="救急要請記録" detail={String(incident.incident_data.requestSituation || incident.created_by || '詳細は救急情報タブで確認')} />)}
        {departures.map(departure => <HistoryRow key={departure.id} date={departure.departure_date} title="退居" detail={[departure.reason, departure.destination_note].filter(Boolean).join(' / ') || '退居記録'} />)}
        <HistoryRow date={resident.updated_at} title="マスター最終更新" detail={resident.master_notes || '-'} />
      </div>
    </div>}
  </div>
  );
};

const HistoryRow = ({ date, title, detail }: { date: string | null; title: string; detail: string }) => (
  <div className="grid gap-1 border-l-2 border-slate-200 py-2 pl-3 sm:grid-cols-[140px_150px_minmax(0,1fr)]">
    <span className="text-slate-500">{formatDateTime(date)}</span>
    <span className="font-semibold text-slate-800">{title}</span>
    <span className="break-words text-slate-600">{detail}</span>
  </div>
);

const InfoBand = ({ label, value, icon }: { label: string; value: string; icon: ReactNode }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-500">{icon}{label}</div>
    <div className="break-words text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

const FieldBlock = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="mb-1 text-xs font-medium text-slate-500">{label}</div>
    <div className="min-h-[38px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">{value}</div>
  </div>
);

const VitalRecordsPanel = ({
  records,
  residentById,
  groups,
  visitDates,
  printProvider,
  loading,
  savingInstructions,
  onPasteClick,
  onVisitDateChange,
  onInstructionChange,
  onSaveInstructions,
  onPrintProvider
}: {
  records: VitalRecord[];
  residentById: Map<string, ResidentMaster>;
  groups: ProviderVitalGroup[];
  visitDates: Record<string, string>;
  printProvider: string;
  loading: boolean;
  savingInstructions: boolean;
  onPasteClick: () => void;
  onVisitDateChange: (provider: string, value: string) => void;
  onInstructionChange: (provider: string, visitDate: string, residentId: string, value: string) => void;
  onSaveInstructions: (provider: string) => void;
  onPrintProvider: (provider: string) => void;
}) => (
  <div className="space-y-4">
    <style>{`
      @media screen {
        .vital-print-text { display: none; }
      }
      @media print {
        @page { size: A4 portrait; margin: 5mm; }
        body { background: #fff !important; }
        #root,
        .resident-management-page,
        .resident-management-main,
        .resident-management-grid,
        .resident-content-panel {
          display: block !important;
          min-height: 0 !important;
          height: auto !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          background: #fff !important;
        }
        .resident-screen-only { display: none !important; }
        body * { visibility: hidden !important; }
        .vital-print-root, .vital-print-root * { visibility: visible !important; }
        .vital-print-root {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          width: 200mm;
          max-width: 200mm;
          background: #fff;
          color: #111827;
          font-size: 9px;
          line-height: 1.16;
        }
        .vital-print-root.space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 0 !important; }
        .vital-screen-only { display: none !important; }
        .vital-print-section {
          break-after: page;
          page-break-after: always;
          break-inside: avoid;
          page-break-inside: avoid;
          border: 0 !important;
          box-shadow: none !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 200mm;
          max-width: 200mm;
          overflow: hidden;
          zoom: var(--vital-print-scale, 1);
        }
        .vital-print-root[data-print-provider]:not([data-print-provider="all"]) .vital-print-section {
          break-after: auto;
          page-break-after: auto;
        }
        .vital-print-section.vital-print-compact { --vital-print-scale: 0.96; }
        .vital-print-section.vital-print-dense { --vital-print-scale: 0.9; }
        .vital-print-section:last-child { break-after: auto; page-break-after: auto; }
        .vital-print-header {
          grid-template-columns: 44mm 1fr !important;
          gap: 6mm !important;
          align-items: center !important;
          margin-bottom: 4px !important;
        }
        .vital-print-provider-label {
          padding: 3px 4px !important;
          font-size: 9px !important;
          line-height: 1.15 !important;
        }
        .vital-print-title {
          font-size: 18px !important;
          line-height: 1.1 !important;
        }
        .vital-print-dates {
          grid-template-columns: 18mm 1fr !important;
          gap: 0 !important;
          max-width: 44mm !important;
          margin-bottom: 5px !important;
          font-size: 9px !important;
          line-height: 1.12 !important;
        }
        .vital-print-table-wrap { border-color: #94a3b8 !important; }
        .vital-print-table {
          font-size: 8.8px !important;
          line-height: 1.14 !important;
        }
        .vital-print-table th {
          padding: 2px 3px !important;
          height: 16px !important;
          line-height: 1.1 !important;
        }
        .vital-print-table td {
          padding: 2px 3px !important;
          height: 18px !important;
          line-height: 1.14 !important;
        }
        .vital-print-name-cell {
          white-space: nowrap !important;
          font-size: 8.7px !important;
        }
        .vital-print-memo,
        .vital-print-instruction {
          font-size: 8.2px !important;
          line-height: 1.18 !important;
        }
        .vital-print-text {
          display: block;
          min-height: 0 !important;
          white-space: pre-wrap;
        }
        .vital-print-input { display: none !important; }
        .vital-print-signatures {
          margin-top: 8px !important;
          gap: 8px !important;
          font-size: 9px !important;
          line-height: 1.24 !important;
        }
        .vital-print-signatures .mt-1 { margin-top: 2px !important; }
      }
    `}</style>

    <div className="rounded-lg border border-slate-200 bg-white p-4 vital-screen-only">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold"><ClipboardList className="h-5 w-5 text-cyan-700" />CSV取込</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onPasteClick}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <ClipboardList className="h-4 w-4" />
            {loading ? '取込中' : 'CSV取込'}
          </button>
        </div>
      </div>
    </div>

    <div className="vital-screen-only rounded-lg border border-cyan-100 bg-cyan-50 px-4 py-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-cyan-950"><Printer className="h-5 w-5 text-cyan-700" />医療機関別 PDF印刷</h2>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {groups.map(group => {
          const visitDate = visitDates[group.provider] ?? group.visitDate;
          return (
            <div key={group.provider} className="rounded-lg border border-cyan-200 bg-white p-3 shadow-sm">
              <div className="mb-2 font-bold text-slate-900">{group.provider}</div>
              <div className="mb-3 grid grid-cols-[88px_1fr] gap-2 text-sm">
                <span className="pt-1 text-slate-500">往診日</span>
                <input
                  type="date"
                  value={visitDate}
                  onChange={event => onVisitDateChange(group.provider, event.target.value)}
                  className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-cyan-700"
                />
                <span className="text-slate-500">記録日</span>
                <span className="font-medium">{formatDateSlash(group.vitalRecordDate)}</span>
                <span className="text-slate-500">利用者</span>
                <span className="font-medium">{group.rows.length}名</span>
              </div>
              <button onClick={() => onPrintProvider(group.provider)} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
                <Printer className="h-4 w-4" />
                PDF印刷
              </button>
            </div>
          );
        })}
      </div>
      {groups.length === 0 && (
        <div className="rounded-lg border border-dashed border-cyan-200 bg-white px-3 py-6 text-center text-sm text-slate-500">
          医療機関割当がありません
        </div>
      )}
    </div>

    <div className="vital-print-root space-y-4" data-print-provider={printProvider}>
      {groups.map(group => (
        <VitalProviderPrintSection
          key={group.provider}
          group={{ ...group, visitDate: visitDates[group.provider] ?? group.visitDate }}
          hiddenInPrint={printProvider !== 'all' && printProvider !== group.provider}
          saving={savingInstructions}
          onVisitDateChange={value => onVisitDateChange(group.provider, value)}
          onInstructionChange={(residentId, value) => onInstructionChange(group.provider, visitDates[group.provider] ?? group.visitDate, residentId, value)}
          onSave={() => onSaveInstructions(group.provider)}
          onPrint={() => onPrintProvider(group.provider)}
        />
      ))}
      {groups.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-slate-500 vital-screen-only">
          医療機関割当がありません
        </div>
      )}
    </div>

    <div className="rounded-lg border border-slate-200 bg-white p-4 vital-screen-only">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold"><HeartPulse className="h-5 w-5 text-cyan-700" />取込済みバイタル履歴</h2>
      </div>
      <VitalTable records={records} residentById={residentById} />
    </div>
  </div>
);

const VitalProviderPrintSection = ({
  group,
  hiddenInPrint,
  saving,
  onVisitDateChange,
  onInstructionChange,
  onSave,
  onPrint
}: {
  group: ProviderVitalGroup;
  hiddenInPrint: boolean;
  saving: boolean;
  onVisitDateChange: (value: string) => void;
  onInstructionChange: (residentId: string, value: string) => void;
  onSave: () => void;
  onPrint: () => void;
}) => (
  <section data-vital-provider={group.provider} className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm vital-print-section ${vitalPrintDensityClass(group.rows.length)} ${hiddenInPrint ? 'hidden print:hidden' : ''}`}>
    <div className="mb-4 grid gap-3 md:grid-cols-[220px_1fr_auto] vital-print-header">
      <div className="rounded-md border border-slate-400 bg-sky-100 px-3 py-2 text-center text-sm font-bold vital-print-provider-label">
        {group.provider}
      </div>
      <div className="text-center text-2xl font-bold tracking-normal vital-print-title">さつきホームバイタル表</div>
      <div className="flex justify-end gap-2 vital-screen-only">
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
          <Save className="h-4 w-4" />
          保存
        </button>
        <button onClick={onPrint} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
          <Printer className="h-4 w-4" />
          PDF印刷
        </button>
      </div>
    </div>

    <div className="mb-4 grid max-w-sm grid-cols-[96px_1fr] gap-y-1 text-sm vital-print-dates">
      <div className="font-semibold">往診日</div>
      <div>
        <input
          type="date"
          value={group.visitDate}
          onChange={event => onVisitDateChange(event.target.value)}
          className="vital-print-input rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-cyan-700"
        />
        <span className="vital-print-text">{formatDateSlash(group.visitDate)}</span>
      </div>
      <div className="font-semibold">バイタル<br />記録日</div>
      <div className="pt-1">{formatDateSlash(group.vitalRecordDate)}</div>
    </div>

    <div className="overflow-hidden rounded-sm border border-slate-400 vital-print-table-wrap">
      <table className="w-full table-fixed border-collapse text-sm vital-print-table">
        <colgroup>
          <col className="w-[15%]" />
          <col className="w-[10%]" />
          <col className="w-[7%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
          <col className="w-[30%]" />
          <col className="w-[22%]" />
        </colgroup>
        <thead>
          <tr className="bg-sky-100 text-center">
            <th className="border border-slate-400 px-2 py-2">利用者名</th>
            <th className="border border-slate-400 px-2 py-2">BP</th>
            <th className="border border-slate-400 px-2 py-2">P</th>
            <th className="border border-slate-400 px-2 py-2">SpO2</th>
            <th className="border border-slate-400 px-2 py-2">KT</th>
            <th className="border border-slate-400 px-2 py-2">メモ</th>
            <th className="border border-slate-400 px-2 py-2">往診時指示</th>
          </tr>
        </thead>
        <tbody>
          {group.rows.map(row => (
            <tr key={row.resident.id} className="align-top">
              <td className="border border-slate-400 bg-sky-50 px-2 py-2 font-medium vital-print-name-cell">{row.resident.name}</td>
              <td className="border border-slate-400 px-2 py-2 text-center">{row.vital?.bp_systolic && row.vital?.bp_diastolic ? `${row.vital.bp_systolic}/${row.vital.bp_diastolic}` : ''}</td>
              <td className="border border-slate-400 px-2 py-2 text-center">{row.vital?.pulse ?? ''}</td>
              <td className="border border-slate-400 px-2 py-2 text-center">{row.vital?.spo2 ? `${row.vital.spo2}%` : ''}</td>
              <td className="border border-slate-400 px-2 py-2 text-center">{row.vital?.temperature ? `${row.vital.temperature}℃` : ''}</td>
              <td className="whitespace-pre-wrap break-words border border-slate-400 px-2 py-2 leading-relaxed vital-print-memo">{row.memo}</td>
              <td className="border border-slate-400 px-2 py-1 vital-print-instruction">
                <textarea
                  value={row.instruction}
                  onChange={event => onInstructionChange(row.resident.id, event.target.value)}
                  rows={2}
                  className="vital-print-input min-h-[48px] w-full resize-y rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-cyan-700"
                />
                <div className="vital-print-text vital-print-instruction-text">{row.instruction}</div>
              </td>
            </tr>
          ))}
          {Array.from({ length: Math.max(0, 6 - group.rows.length) }).map((_, index) => (
            <tr key={`blank-${index}`} className="h-9">
              <td className="border border-slate-400 bg-sky-50 px-2 py-2">&nbsp;</td>
              <td className="border border-slate-400 px-2 py-2" />
              <td className="border border-slate-400 px-2 py-2" />
              <td className="border border-slate-400 px-2 py-2" />
              <td className="border border-slate-400 px-2 py-2" />
              <td className="border border-slate-400 px-2 py-2" />
              <td className="border border-slate-400 px-2 py-2" />
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="mt-8 space-y-8 text-sm font-semibold vital-print-signatures">
      <div>
        <div>・担当看護職員（署名か捺印）</div>
        <div className="mt-1">担当医に対し上記利用者の健康状態について情報提供します。</div>
      </div>
      <div>
        <div>・担当医（署名か捺印）</div>
        <div className="mt-1">上記利用者の健康状態について十分な情報提供をうけ上記指示を行いました。</div>
      </div>
    </div>
  </section>
);

const VitalTable = ({ records, residentById }: { records: VitalRecord[]; residentById?: Map<string, ResidentMaster> }) => (
  <div className="max-h-[60vh] overflow-auto overscroll-contain rounded-lg border border-slate-100">
    <table className="w-full table-fixed text-sm">
      <colgroup>
        <col className="w-[14%]" />
        <col className="w-[13%]" />
        <col className="w-[8%]" />
        <col className="w-[6%]" />
        <col className="w-[7%]" />
        <col className="w-[6%]" />
        <col className="w-[11%]" />
        <col className="w-[35%]" />
      </colgroup>
      <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm">
        <tr>
          <th className="px-3 py-2">記録日時</th>
          <th className="px-3 py-2">入居者</th>
          <th className="px-3 py-2">BP</th>
          <th className="px-3 py-2">P</th>
          <th className="px-3 py-2">SpO2</th>
          <th className="px-3 py-2">KT</th>
          <th className="px-3 py-2">報告者</th>
          <th className="px-3 py-2">内容</th>
        </tr>
      </thead>
      <tbody>
        {records.map(record => {
          const resident = record.resident_id && residentById ? residentById.get(record.resident_id) : null;
          return (
            <tr key={record.id} className="border-t border-slate-100 align-top">
              <td className="break-words px-3 py-2">{formatDateTime(record.recorded_at)}</td>
              <td className="break-words px-3 py-2 font-medium">{resident?.name ?? record.resident_name_snapshot}</td>
              <td className="px-3 py-2">{record.bp_systolic && record.bp_diastolic ? `${record.bp_systolic}/${record.bp_diastolic}` : '-'}</td>
              <td className="px-3 py-2">{record.pulse ?? '-'}</td>
              <td className="px-3 py-2">{record.spo2 ?? '-'}</td>
              <td className="px-3 py-2">{record.temperature ?? '-'}</td>
              <td className="break-words px-3 py-2">{record.reporter_name ?? '-'}</td>
              <td className="whitespace-pre-wrap break-words px-3 py-2 text-slate-600">{record.content ?? '-'}</td>
            </tr>
          );
        })}
        {records.length === 0 && (
          <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={8}>バイタル記録がありません</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const IssuesPanel = ({ issues, submissions, residentById }: { issues: ImportIssue[]; submissions: EmployeeSubmission[]; residentById: Map<string, ResidentMaster> }) => (
  <div className="space-y-4">
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold"><AlertTriangle className="h-5 w-5 text-red-700" />要確認</h2>
      <div className="max-h-[60vh] overflow-auto overscroll-contain">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm">
            <tr>
              <th className="px-3 py-2">種別</th>
              <th className="px-3 py-2">氏名</th>
              <th className="px-3 py-2">紐付き</th>
              <th className="px-3 py-2">元データ</th>
              <th className="px-3 py-2">発生日</th>
            </tr>
          </thead>
          <tbody>
            {issues.map(issue => {
              const resident = issue.resident_id ? residentById.get(issue.resident_id) : null;
              return (
                <tr key={issue.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{issueLabels[issue.issue_type] ?? issue.issue_type}</td>
                  <td className="px-3 py-2">{issue.resident_name_snapshot || issue.normalized_name || '-'}</td>
                  <td className="px-3 py-2">{resident ? `${resident.name} / ${statusLabels[resident.status]}` : '-'}</td>
                  <td className="px-3 py-2">{issue.source_system || '-'} {issue.source_row ? `#${issue.source_row}` : ''}</td>
                  <td className="px-3 py-2">{formatDateTime(issue.created_at)}</td>
                </tr>
              );
            })}
            {issues.length === 0 && (
              <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={5}>要確認はありません</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold"><ClipboardCheck className="h-5 w-5 text-cyan-700" />入退去登録履歴</h2>
      <div className="max-h-[60vh] overflow-auto overscroll-contain">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm">
            <tr>
              <th className="px-3 py-2">日時</th>
              <th className="px-3 py-2">区分</th>
              <th className="px-3 py-2">氏名</th>
              <th className="px-3 py-2">状態</th>
              <th className="px-3 py-2">結果</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map(submission => (
              <tr key={submission.id} className="border-t border-slate-100">
                <td className="whitespace-nowrap px-3 py-2">{formatDateTime(submission.created_at)}</td>
                <td className="px-3 py-2 font-medium">{submission.submission_type === 'admission' ? '入居' : '退居'}</td>
                <td className="px-3 py-2">{submission.resident_name_snapshot}</td>
                <td className="px-3 py-2">{submission.status === 'applied' ? '反映済み' : '要確認'}</td>
                <td className="px-3 py-2">{submission.result_message || '-'}</td>
              </tr>
            ))}
            {submissions.length === 0 && (
              <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={5}>登録履歴はありません</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const IssueListRow = ({ issue }: { issue: ImportIssue }) => (
  <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
    <div className="font-semibold text-red-900">{issue.resident_name_snapshot || issue.normalized_name || '-'}</div>
    <div className="mt-1 text-xs text-red-700">{issueLabels[issue.issue_type] ?? issue.issue_type}</div>
  </div>
);

const issueResidentRows = (issues: ImportIssue[], _residentById: Map<string, ResidentMaster>): ImportIssue[] => issues;

const ResidentModal = ({
  draft,
  medicalProviderOptions,
  onChange,
  onClose,
  onSave
}: {
  draft: ResidentDraft;
  medicalProviderOptions: string[];
  onChange: (draft: ResidentDraft) => void;
  onClose: () => void;
  onSave: () => void;
}) => (
  <ModalShell title={draft.id ? '入居者編集' : '新規入居者'} icon={<UserRound className="h-5 w-5" />} onClose={onClose}>
    <div className="grid gap-3 md:grid-cols-2">
      <TextField label="氏名" value={draft.name} onChange={value => onChange({ ...draft, name: value })} />
      <TextField label="かな" value={draft.kana} onChange={value => onChange({ ...draft, kana: value })} />
      <TextField label="表示順" value={draft.display_order} onChange={value => onChange({ ...draft, display_order: value })} />
      <TextField label="居室" value={draft.room} onChange={value => onChange({ ...draft, room: value })} />
      <SelectField label="状態" value={draft.status} onChange={value => onChange({ ...draft, status: value as ResidentStatus })} options={[['active', '入居中'], ['retired', '退居'], ['inactive', '非表示']]} />
      <TextField label="介護度" value={draft.care_level} onChange={value => onChange({ ...draft, care_level: value })} />
      <TextField label="性別" value={draft.gender} onChange={value => onChange({ ...draft, gender: value })} />
      <TextField label="生年月日" type="date" value={draft.birth_date} onChange={value => onChange({ ...draft, birth_date: value })} />
      <TextField label="入居日" type="date" value={draft.admission_date} onChange={value => onChange({ ...draft, admission_date: value })} />
      <TextField label="入居元" value={draft.admission_source_note} onChange={value => onChange({ ...draft, admission_source_note: value })} />
      <TextField label="利用者キー" value={draft.care_record_user_key} onChange={value => onChange({ ...draft, care_record_user_key: value })} />
      <TextField label="保険者名" value={draft.insurer_name} onChange={value => onChange({ ...draft, insurer_name: value })} />
      <TextField label="介護保険証期限" type="date" value={draft.care_insurance_expiry_date} onChange={value => onChange({ ...draft, care_insurance_expiry_date: value })} />
      <SelectField label="担当クリニック" value={draft.primary_clinic} onChange={value => onChange({ ...draft, primary_clinic: value })} options={providerSelectOptions(medicalProviderOptions, draft.primary_clinic)} />
      <TextField label="緊急時の病院" value={draft.emergency_hospital} onChange={value => onChange({ ...draft, emergency_hospital: value })} />
      <TextField label="往診間隔(日)" value={draft.visit_interval_days} onChange={value => onChange({ ...draft, visit_interval_days: value })} />
      <TextField label="最終往診日" type="date" value={draft.last_visit_date} onChange={value => onChange({ ...draft, last_visit_date: value })} />
      <TextField label="次回往診日" type="date" value={draft.next_visit_date} onChange={value => onChange({ ...draft, next_visit_date: value })} />
      <TextArea label="共有事項" value={draft.master_notes} onChange={value => onChange({ ...draft, master_notes: value })} />
      <TextArea label="メモ" value={draft.notes} onChange={value => onChange({ ...draft, notes: value })} />
    </div>
    <ModalActions onClose={onClose} onSave={onSave} />
  </ModalShell>
);

const MedicalProviderModal = ({
  providers,
  draft,
  onDraftChange,
  onNew,
  onEdit,
  onClose,
  onSave
}: {
  providers: MedicalProvider[];
  draft: MedicalProviderDraft | null;
  onDraftChange: (draft: MedicalProviderDraft | null) => void;
  onNew: () => void;
  onEdit: (provider: MedicalProvider) => void;
  onClose: () => void;
  onSave: () => void;
}) => (
  <ModalShell title="クリニック管理" icon={<Building2 className="h-5 w-5" />} onClose={onClose}>
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="max-h-[60vh] overflow-auto overscroll-contain rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs text-slate-500 shadow-sm">
            <tr>
              <th className="px-3 py-2">表示順</th>
              <th className="px-3 py-2">医療機関名</th>
              <th className="px-3 py-2">状態</th>
              <th className="px-3 py-2 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {providers.map(provider => (
              <tr key={provider.id} className="border-t border-slate-100">
                <td className="px-3 py-2">{provider.display_order ?? '-'}</td>
                <td className="px-3 py-2 font-medium">{provider.name}</td>
                <td className="px-3 py-2">{provider.active ? '有効' : '無効'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => onEdit(provider)} className="rounded-md px-2 py-1 text-cyan-700 hover:bg-cyan-50">編集</button>
                </td>
              </tr>
            ))}
            {providers.length === 0 && (
              <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={4}>クリニックが未登録です</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="font-bold">{draft?.id ? '編集' : '新規追加'}</h3>
          <button onClick={onNew} className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-800">
            <Plus className="h-4 w-4" />
            新規
          </button>
        </div>
        {draft ? (
          <div className="space-y-3">
            <TextField label="医療機関名" value={draft.name} onChange={value => onDraftChange({ ...draft, name: value })} />
            <TextField label="表示順" value={draft.display_order} onChange={value => onDraftChange({ ...draft, display_order: value })} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={draft.active} onChange={event => onDraftChange({ ...draft, active: event.target.checked })} />
              従業員ページのプルダウンに表示
            </label>
            <TextArea label="メモ" value={draft.notes} onChange={value => onDraftChange({ ...draft, notes: value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => onDraftChange(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-white">キャンセル</button>
              <button onClick={onSave} className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-800">
                <CheckCircle2 className="h-4 w-4" />
                保存
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-6 text-center text-sm text-slate-500">
            行を編集するか、新規を押してください
          </div>
        )}
      </div>
    </div>
  </ModalShell>
);

const RetirementModal = ({ resident, draft, onChange, onClose, onSave }: { resident: ResidentMaster; draft: RetirementDraft; onChange: (draft: RetirementDraft) => void; onClose: () => void; onSave: () => void }) => (
  <ModalShell title={`${resident.name}さんの退居登録`} icon={<UserX className="h-5 w-5" />} onClose={onClose}>
    <div className="space-y-3">
      <TextField label="退居日" type="date" value={draft.departure_date} onChange={value => onChange({ ...draft, departure_date: value })} />
      <SelectField label="退居時の介護度" value={draft.departure_care_level} onChange={value => onChange({ ...draft, departure_care_level: value })} options={careLevelSelectOptions} />
      <TextField label="退居理由" value={draft.reason} onChange={value => onChange({ ...draft, reason: value })} />
      <TextArea label="共有事項" value={draft.destination_note} onChange={value => onChange({ ...draft, destination_note: value })} />
    </div>
    <ModalActions onClose={onClose} onSave={onSave} saveLabel="退居登録" />
  </ModalShell>
);

const AssignmentModal = ({
  draft,
  medicalProviderOptions,
  onChange,
  onClose,
  onSave
}: {
  draft: AssignmentDraft;
  medicalProviderOptions: string[];
  onChange: (draft: AssignmentDraft) => void;
  onClose: () => void;
  onSave: () => void;
}) => (
  <ModalShell title="医療機関割当" icon={<Stethoscope className="h-5 w-5" />} onClose={onClose}>
    <div className="grid gap-3 md:grid-cols-2">
      <SelectField label="医療機関名" value={draft.medical_provider_name} onChange={value => onChange({ ...draft, medical_provider_name: value })} options={providerSelectOptions(medicalProviderOptions, draft.medical_provider_name)} />
      <TextField label="元シート" value={draft.source_sheet_title} onChange={value => onChange({ ...draft, source_sheet_title: value })} />
      <TextField label="次回往診日" type="date" value={draft.next_visit_date} onChange={value => onChange({ ...draft, next_visit_date: value })} />
      <TextField label="最新記録日" type="date" value={draft.latest_vital_record_date} onChange={value => onChange({ ...draft, latest_vital_record_date: value })} />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={draft.active} onChange={event => onChange({ ...draft, active: event.target.checked })} />
        有効
      </label>
      <TextArea label="メモ" value={draft.notes} onChange={value => onChange({ ...draft, notes: value })} />
    </div>
    <ModalActions onClose={onClose} onSave={onSave} />
  </ModalShell>
);

const VitalModal = ({ draft, onChange, onClose, onSave }: { draft: VitalDraft; onChange: (draft: VitalDraft) => void; onClose: () => void; onSave: () => void }) => (
  <ModalShell title="バイタル入力" icon={<HeartPulse className="h-5 w-5" />} onClose={onClose}>
    <div className="grid gap-3 md:grid-cols-2">
      <TextField label="記録日時" type="datetime-local" value={draft.recorded_at} onChange={value => onChange({ ...draft, recorded_at: value })} />
      <TextField label="報告者" value={draft.reporter_name} onChange={value => onChange({ ...draft, reporter_name: value })} />
      <TextField label="BP 上" value={draft.bp_systolic} onChange={value => onChange({ ...draft, bp_systolic: value })} />
      <TextField label="BP 下" value={draft.bp_diastolic} onChange={value => onChange({ ...draft, bp_diastolic: value })} />
      <TextField label="P" value={draft.pulse} onChange={value => onChange({ ...draft, pulse: value })} />
      <TextField label="SpO2" value={draft.spo2} onChange={value => onChange({ ...draft, spo2: value })} />
      <TextField label="KT" value={draft.temperature} onChange={value => onChange({ ...draft, temperature: value })} />
      <TextArea label="内容" value={draft.content} onChange={value => onChange({ ...draft, content: value })} />
    </div>
    <ModalActions onClose={onClose} onSave={onSave} />
  </ModalShell>
);

const ModalShell = ({ title, icon, children, onClose }: { title: string; icon: ReactNode; children: ReactNode; onClose: () => void }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 px-4 py-6">
    <div className="max-h-[92vh] w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-xl">
      <div className="sticky top-0 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
        <h2 className="flex items-center gap-2 text-lg font-bold">{icon}{title}</h2>
        <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100" aria-label="閉じる">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const ModalActions = ({ onClose, onSave, saveLabel = '保存' }: { onClose: () => void; onSave: () => void; saveLabel?: string }) => (
  <div className="mt-5 flex justify-end gap-2">
    <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">閉じる</button>
    <button onClick={onSave} className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800">
      <CheckCircle2 className="h-4 w-4" />
      {saveLabel}
    </button>
  </div>
);

const TextField = ({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; type?: string }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-700"
    />
  </label>
);

const TextArea = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
  <label className="block md:col-span-2">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <textarea
      value={value}
      onChange={event => onChange(event.target.value)}
      rows={3}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-700"
    />
  </label>
);

const SelectField = ({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    <select
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-700"
    >
      {options.map(([optionValue, labelText]) => (
        <option key={optionValue} value={optionValue}>{labelText}</option>
      ))}
    </select>
  </label>
);

const parseCsv = (text: string): Record<string, string>[] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      row.push(cell);
      cell = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') i += 1;
      row.push(cell);
      if (row.some(value => value.trim() !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some(value => value.trim() !== '')) rows.push(row);
  const [headers = [], ...body] = rows;
  return body.map(values => Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ''])));
};

export default ResidentManagementPage;
